import { Loading } from "@/components/Loading/Loading";
import { TermsAndPolicyDialog } from "@/components/TermsAndPolicyDialog/TermsAndPolicyDialog";
import { USER_METADATA_URL } from "@/constants/userMetadata";
import { selectedTermsAccepted, setTermsAccepted } from "@/store/slices/termsAcceptedSlice";
import { RootState, store } from "@/store/store";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { FC, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface Props {
  children: ReactNode;
}

/**
 * TermsGuard conditionally renders an "Accept terms" dialog over the
 * children node(s) based on if the user has accepted the Asiga Terms
 * & Conditions and Privacy Policy.
 */
export const TermsGuard: FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  // Default to false to prevent flicker between routes
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const termsAccepted = useSelector((state: RootState) => selectedTermsAccepted(state));

  useEffect(() => {
    const getTermsAccepted = async () => {
      setIsLoading(true);
      try {
        const accessToken = await getAccessTokenSilently();

        const decoded = jwtDecode<
          JwtPayload & {
            permissions: string[];
            [USER_METADATA_URL]: {
              terms_and_privacy_accepted: boolean;
            };
          }
        >(accessToken);

        const termsPrivacyAccepted = decoded?.[USER_METADATA_URL]?.terms_and_privacy_accepted === true;
        store.dispatch(setTermsAccepted(termsPrivacyAccepted));
      } catch (_) {
        // Ignore access token errors. Auth0 will handle re-fetching a valid token.
      }
      setIsLoading(false);
    };
    if (termsAccepted === undefined) {
      getTermsAccepted();
    } else {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, termsAccepted, t]);

  if (isLoading && termsAccepted === undefined) {
    return <Loading />;
  }

  return (
    <>
      {termsAccepted === false && <TermsAndPolicyDialog />}
      {children}
    </>
  );
};
